import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { FiTrash2, FiMinus, FiPlus, FiShoppingBag, FiArrowLeft, FiTag, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import './Cart.css';

// Base URL for serving static files (images)
const getBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') return '';
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  return apiUrl.replace(/\/api\/?$/, '');
};

const VALID_COUPONS = {
  PET10: { discount: 0.10, label: '10% off' }
};

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal, getItemKey } = useCart();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return 'https://via.placeholder.com/100x100?text=No+Image';
    if (imageUrl.startsWith('/uploads/')) {
      return `${getBaseUrl()}${imageUrl}`;
    }
    return imageUrl;
  };

  const applyCoupon = () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) {
      toast.error('Please enter a coupon code');
      return;
    }
    const coupon = VALID_COUPONS[code];
    if (coupon) {
      setAppliedCoupon({ code, ...coupon });
      setCouponCode('');
      // coupon applied
    } else {
      toast.error('Invalid coupon code');
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    // coupon removed
  };

  if (cart.length === 0) {
    return (
      <div className="empty-cart">
        <div className="container">
          <div className="empty-cart-content">
            <FiShoppingBag size={64} />
            <h2>Your cart is empty</h2>
            <p>Looks like you haven't added any items to your cart yet.</p>
            <Link to="/shop" className="btn btn-primary">
              Start Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleCheckout = () => {
    navigate('/checkout', { state: { coupon: appliedCoupon } });
  };

  const subtotal = getCartTotal();
  const discountAmount = appliedCoupon ? Math.round(subtotal * appliedCoupon.discount) : 0;
  const afterDiscount = subtotal - discountAmount;
  const shipping = afterDiscount >= 999 ? 0 : 50;
  const total = afterDiscount + shipping;

  return (
    <div className="cart-page">
      <div className="container">
        <div className="cart-header">
          <Link to="/shop" className="back-link">
            <FiArrowLeft />
            Continue Shopping
          </Link>
          <div className="cart-title-row">
            <h1 className="page-title">Shopping Cart</h1>
            <span className="cart-count">({cart.length} {cart.length === 1 ? 'item' : 'items'})</span>
          </div>
        </div>

        <div className="cart-content">
          <div className="cart-items">
            {cart.map((item) => {
              const itemKey = getItemKey(item);
              return (
                <div key={itemKey} className="cart-item">
                  <img 
                    src={getImageUrl(item.imageUrl)} 
                    alt={item.name} 
                    className="cart-item-image" 
                  />
                  <div className="cart-item-mid">
                    <h3 className="cart-item-name">{item.name}</h3>
                    <span className="cart-item-weight">{item.weight}</span>
                  </div>
                  <div className="cart-item-right">
                    <span className="cart-item-price">₹{(item.price * item.quantity).toLocaleString()}</span>
                    <div className="quantity-control">
                      <button 
                        onClick={() => updateQuantity(itemKey, item.quantity - 1)}
                        className="quantity-btn"
                        disabled={item.quantity <= 1}
                      >
                        <FiMinus />
                      </button>
                      <span className="quantity">{item.quantity}</span>
                      <button 
                        onClick={() => updateQuantity(itemKey, item.quantity + 1)}
                        className="quantity-btn"
                        disabled={item.quantity >= item.stock}
                      >
                        <FiPlus />
                      </button>
                    </div>
                    <button 
                      onClick={() => removeFromCart(itemKey)}
                      className="remove-btn"
                      aria-label="Remove item"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="cart-summary">
            <h2>Order Summary</h2>

            {/* Coupon Code Section */}
            <div className="coupon-section">
              {appliedCoupon ? (
                <div className="coupon-applied">
                  <div className="coupon-applied-info">
                    <FiTag />
                    <span><strong>{appliedCoupon.code}</strong> — {appliedCoupon.label}</span>
                  </div>
                  <button onClick={removeCoupon} className="coupon-remove-btn" aria-label="Remove coupon">
                    <FiX />
                  </button>
                </div>
              ) : (
                <div className="coupon-input-group">
                  <input
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                    className="coupon-input"
                  />
                  <button onClick={applyCoupon} className="coupon-apply-btn">Apply</button>
                </div>
              )}
            </div>
            
            <div className="summary-details">
              <div className="summary-row">
                <span>Subtotal</span>
                <span>₹{subtotal.toLocaleString()}</span>
              </div>

              {appliedCoupon && (
                <div className="summary-row discount-row">
                  <span>Discount ({appliedCoupon.label})</span>
                  <span>−₹{discountAmount.toLocaleString()}</span>
                </div>
              )}

              <div className="summary-row">
                <span>Shipping</span>
                <span className={shipping === 0 ? 'free-shipping' : ''}>
                  {shipping === 0 ? 'FREE' : `₹${shipping}`}
                </span>
              </div>

              {afterDiscount < 999 && (
                <div className="shipping-notice">
                  Add ₹{(999 - afterDiscount).toLocaleString()} more for free shipping
                </div>
              )}

              <div className="summary-row total">
                <span>Total</span>
                <span>₹{total.toLocaleString()}</span>
              </div>
            </div>

            <button onClick={handleCheckout} className="btn btn-primary checkout-btn">
              Proceed to Checkout
            </button>

            <p className="secure-note">
              Fast delivery to your doorstep
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
